"use strict";

// The Renault Academy training file, read the way the backend reads it.
//
// The sheet that matters is Service Advisors. The Service Managers sheet is ignored, because a
// manager never receives a card. Accreditation is granted by the "NEW: Brands & Products" column
// reading Successful, and the text is carried across as it reads rather than turned into a flag
// here, so the import can be looked at later and say exactly what the file said.

// The headings as the Academy writes them, with the spellings the file has arrived under.
const COLUMN_ALIASES = {
     email: ["e-mail", "email", "e mail"],
     username: ["username", "user name"],
     firstName: ["first name", "firstname"],
     lastName: ["last name", "lastname"],
     dealerCode: ["dealer code", "dealercode"],
     dealerName: ["dealer name", "dealername"],
     brandsAndProductsStatus: ["new: brands & products", "brands & products", "new: brands and products"],
     jobRole: ["job role", "jobrole"],
};

// A row is the header when it names the column nothing can be matched without. The heading is
// matched on what it starts with rather than what it equals, because the Academy appends notes to
// its own column titles.
const REQUIRED_HEADER_FIELDS = ["email"];

export const ACCREDITED_STATUS = "Successful";

export const SERVICE_ADVISORS_SHEET = "Service Advisors";

const headerMatches = (heading, field) => {
     const key = String(heading ?? "")
          .trim()
          .toLowerCase();

     if (!key) {
          return false;
     }

     return COLUMN_ALIASES[field].some(
          (alias) => key === alias || key.startsWith(alias),
     );
};

export const readHeaderColumns = (cells) => {
     const columns = {};

     (cells || []).forEach((cell, index) => {
          Object.keys(COLUMN_ALIASES).forEach((field) => {
               if (columns[field] === undefined && headerMatches(cell, field)) {
                    columns[field] = index;
               }
          });
     });

     const isHeader = REQUIRED_HEADER_FIELDS.every(
          (field) => columns[field] !== undefined,
     );

     return isHeader ? columns : null;
};

const readCell = (cells, columns, field) => {
     const index = columns[field];

     if (index === undefined) {
          return "";
     }

     return String(cells[index] ?? "").trim();
};

export const buildRow = (cells, columns, rowNumber) => {
     return {
          rowNumber,
          email: readCell(cells, columns, "email"),
          username: readCell(cells, columns, "username"),
          firstName: readCell(cells, columns, "firstName"),
          lastName: readCell(cells, columns, "lastName"),
          dealerCode: readCell(cells, columns, "dealerCode"),
          dealerName: readCell(cells, columns, "dealerName"),
          brandsAndProductsStatus: readCell(
               cells,
               columns,
               "brandsAndProductsStatus",
          ),
     };
};

// A row with no email at all is one of the file's empty shells. It names nobody, so it is dropped
// before the grid rather than shown as a row to fix.
export const isEmptyShell = (row) =>
     !row.email && !row.firstName && !row.lastName && !row.dealerCode;

// Read from the row as it stands now, not as it arrived, so correcting a cell clears its problem
// and a corrected row becomes loadable without re-reading the file.
export const rowProblem = (row) => {
     const problems = [];
     const email = String(row.email || "").trim();

     if (!email) {
          problems.push("Email is missing, and it is how an advisor is matched.");
     } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          problems.push("That email does not look like an email address.");
     }

     // A blank status is not a problem with the row. It grants nothing, which the Grants column
     // already says, and the advisor may still be new to Equipe and need an account and an invite.
     // Deciding that is the backend's job, so the row is sent.
     return problems.join(" ");
};

export const isAccredited = (row) =>
     String(row.brandsAndProductsStatus || "").trim().toLowerCase() ===
     ACCREDITED_STATUS.toLowerCase();

export const toCommitRows = (rows) =>
     rows.map((row) => ({
          rowNumber: row.rowNumber,
          email: String(row.email || "").trim(),
          username: String(row.username || "").trim(),
          firstName: String(row.firstName || "").trim(),
          lastName: String(row.lastName || "").trim(),
          dealerCode: String(row.dealerCode || "").trim(),
          dealerName: String(row.dealerName || "").trim(),
          brandsAndProductsStatus: String(
               row.brandsAndProductsStatus || "",
          ).trim(),
     }));
